import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Globe2, Layout, Plus, Rocket, Target, Users } from "lucide-react";

import { AdminLayout } from "@/components/admin/layout/AdminLayout";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { blogAdminService } from "@/services/blogAdminService";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import type { BlogPost } from "@/types/blog";
import { fetchAllRoleRecords } from "@/services/adminRoleService";

type TeamHierarchy = Array<{ role: string; members: string[] }>;

const pageLibrary = [
  {
    title: "Página Inicial",
    seoScore: 92,
    ranking: "Topo 1 · “Toc Toc Produção”",
    status: "Publicado",
    channel: "Site principal",
  },
  {
    title: "Serviços",
    seoScore: 88,
    ranking: "Topo 3 · “Produtora Toc Toc”",
    status: "Publicado",
    channel: "Site principal",
  },
  {
    title: "Blog · Últimas histórias",
    seoScore: 86,
    ranking: "Topo 5 · “Blog Toc Toc”",
    status: "Em evolução",
    channel: "Blog institucional",
  },
  {
    title: "FAQ",
    seoScore: 80,
    ranking: "Topo 5 · “Dúvidas Toc Toc”",
    status: "Publicado",
    channel: "Site principal",
  },
  {
    title: "Contato",
    seoScore: 84,
    ranking: "Topo 3 · “Contato Toc Toc”",
    status: "Publicado",
    channel: "Site principal",
  },
];

const seoHighlights = [
  { label: "Score médio SEO", value: "89", trend: "+4.1%" },
  { label: "Palavras-chave em destaque", value: "18", trend: "+6" },
  { label: "CTR orgânico médio", value: "5,8%", trend: "+1,1pp" },
];

const publishingChannels = [
  { label: "Site principal", value: 12 },
  { label: "Blog", value: 38 },
  { label: "Landing pages", value: 6 },
];

const automationStatus = [
  {
    title: "Rotina de SEO",
    description: "Checklist semanal de metadados e performance orgânica",
    status: "No prazo",
  },
  {
    title: "Distribuição omnichannel",
    description: "Postagens sincronizadas com LinkedIn, Instagram e Newsletter",
    status: "Automatizado",
  },
  {
    title: "Auditoria de acessibilidade",
    description: "Próxima rodada programada para 24/07",
    status: "Agendado",
  },
];

const fallbackTeam: TeamHierarchy = [
  { role: "Super Admin", members: ["Central Toc Toc"] },
  { role: "Administradores", members: ["Equipe estratégica"] },
  { role: "Editores", members: ["Time de conteúdo"] },
];

export default function AdminHome() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAdminAuth();
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
  });
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<Array<{ label: string; value: number }>>([]);
  const [teamHierarchy, setTeamHierarchy] = useState<TeamHierarchy>(fallbackTeam);
  const [loadingTeam, setLoadingTeam] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/admin-login");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      void loadStats();
      void loadTeamStructure();
    }
  }, [isAdmin]);

  async function loadStats() {
    try {
      const { posts } = await blogAdminService.listPosts({ limit: 200 });
      const publishedPosts = posts.filter(post => post.published);
      const draftPosts = posts.filter(post => !post.published);

      setStats({
        total: posts.length,
        published: publishedPosts.length,
        draft: draftPosts.length,
      });

      setRecentPosts(posts.slice(0, 4));

      const categoryCount = posts.reduce<Record<string, number>>((acc, post) => {
        const key = (post.category ?? "Outros").trim() || "Outros";
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      }, {});

      const breakdown = Object.entries(categoryCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([label, value]) => ({ label, value }));

      setCategoryBreakdown(breakdown);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  }

  async function loadTeamStructure() {
    try {
      const [{ data: usersData, error: usersError }, { records: roleRecords }] = await Promise.all([
        supabase
          .from('users')
          .select('id, email, role')
          .order('email', { ascending: true }),
        fetchAllRoleRecords(),
      ]);

      if (usersError) throw usersError;

      if (!roleRecords.length && !usersData?.length) {
        setTeamHierarchy(fallbackTeam);
        return;
      }

      const emailById = new Map<string, { email: string; fallbackRole: string | null }>();
      (usersData ?? []).forEach(user => {
        emailById.set(user.id, {
          email: user.email ?? 'Acesso interno',
          fallbackRole: user.role ?? null,
        });
      });

      const grouped = roleRecords.reduce<Record<string, Set<string>>>((acc, row) => {
        const meta = row.user_id ? emailById.get(row.user_id) : null;
        const email = meta?.email ?? row.email ?? 'Conta sem email';
        const key = row.role ?? meta?.fallbackRole ?? 'viewer';
        if (!acc[key]) {
          acc[key] = new Set();
        }
        if (email) {
          acc[key].add(email);
        }
        return acc;
      }, {});

      if (!roleRecords.length && usersData?.length) {
        usersData.forEach(user => {
          const key = user.role ?? 'viewer';
          if (!grouped[key]) {
            grouped[key] = new Set();
          }
          if (user.email) {
            grouped[key].add(user.email);
          }
        });
      }

      const roleOrder = ['super_admin', 'admin', 'editor', 'viewer'];

      const ordered = Object.entries(grouped)
        .sort(([roleA], [roleB]) => {
          const indexA = roleOrder.indexOf(roleA);
          const indexB = roleOrder.indexOf(roleB);
          if (indexA === -1 && indexB === -1) return roleA.localeCompare(roleB);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        .map(([role, members]) => ({
          role:
            role === 'super_admin'
              ? 'Super Admin'
              : role === 'admin'
              ? 'Administradores'
              : role === 'editor'
              ? 'Editores'
              : 'Visualizadores',
          members: Array.from(members),
        }));

      setTeamHierarchy(ordered.length ? ordered : fallbackTeam);
    } catch (error) {
      console.error("Erro ao carregar hierarquia de usuários", error);
      setTeamHierarchy(fallbackTeam);
    } finally {
      setLoadingTeam(false);
    }
  }

  const publishedRatio = useMemo(() => {
    if (stats.total === 0) return 0;
    return Math.round((stats.published / stats.total) * 100);
  }, [stats]);

  if (authLoading || !isAdmin) {
    return null;
  }

  const quickCards = [
    {
      title: "Posts publicados",
      value: stats.published,
      description: `${publishedRatio}% de todo o conteúdo ativo`,
      icon: Globe2,
    },
    {
      title: "Rascunhos em evolução",
      value: stats.draft,
      description: "Prontos para revisão final",
      icon: CalendarDays,
    },
    {
      title: "Biblioteca de páginas",
      value: pageLibrary.length,
      description: "Experiências institucionais mapeadas",
      icon: Layout,
    },
  ];

  return (
    <AdminLayout
      title="Central de Inteligência Toc Toc"
      description="Visão completa de conteúdo, performance e hierarquia para guiar decisões estratégicas."
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Central" },
      ]}
      status={{ tone: "success", label: "Ambiente monitorado em tempo real" }}
      actions={
        <Fragment>
          <Button variant="outline" asChild>
            <Link to="/admin/health">Status do projeto</Link>
          </Button>
          <Button asChild>
            <Link to="/admin/posts/new">
              <Plus className="mr-2 h-4 w-4" />
              Novo Post
            </Link>
          </Button>
        </Fragment>
      }
    >
      <section className="grid gap-6 lg:grid-cols-3">
        {quickCards.map(({ title, value, description, icon: Icon }) => (
          <Card key={title} className="border-slate-200 bg-white text-slate-900 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
                <CardDescription className="text-xs text-slate-500">{description}</CardDescription>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FF5605]/10 text-[#FF5605]">
                <Icon className="h-5 w-5" />
              </span>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-semibold tracking-tight">{value}</p>
              {title === "Posts publicados" && (
                <div className="mt-4 space-y-2">
                  <Progress value={publishedRatio} className="h-2" />
                  <p className="text-xs text-slate-500">Meta: manter acima de 75% de conteúdo atualizado</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader className="flex flex-col gap-2 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">Biblioteca de páginas e SEO</CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Governança das principais experiências do site, com indicadores de SEO e ranqueamento.
                </CardDescription>
              </div>
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-xs text-slate-600">
                {pageLibrary.length} páginas monitoradas
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {pageLibrary.map(page => (
              <div
                key={page.title}
                className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-4"
              >
                <div className="sm:col-span-2">
                  <p className="text-sm font-semibold text-slate-900">{page.title}</p>
                  <p className="text-xs text-slate-500">{page.channel}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">SEO</p>
                  <p className="text-sm font-semibold text-slate-900">{page.seoScore}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Ranqueamento</p>
                  <p className="text-sm font-semibold text-slate-900">{page.ranking}</p>
                </div>
                <div className="sm:col-span-4">
                  <Badge className="bg-emerald-50 text-emerald-600">
                    {page.status}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Performance orgânica</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Métricas consolidadas da última semana
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {seoHighlights.map(item => (
              <div key={item.label} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">{item.label}</p>
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-2xl font-semibold text-slate-900">{item.value}</span>
                  <Badge className="bg-emerald-50 text-emerald-600">{item.trend}</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader className="flex flex-col gap-2 pb-4">
            <CardTitle className="text-lg">Movimento do blog</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Últimas publicações e distribuição por tema.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              {recentPosts.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum post cadastrado ainda.</p>
              ) : (
                recentPosts.map(post => (
                  <div key={post.id ?? post.slug} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-600">
                          {post.published ? "Publicado" : "Rascunho"}
                        </Badge>
                        {post.category && <span>{post.category}</span>}
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900">{post.title}</h3>
                      {post.excerpt && (
                        <p className="text-xs text-slate-500 line-clamp-2">{post.excerpt}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {categoryBreakdown.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Distribuição por tema</h4>
                <div className="mt-3 space-y-2">
                  {categoryBreakdown.map(item => (
                    <div key={item.label} className="flex items-center justify-between text-xs text-slate-600">
                      <span>{item.label}</span>
                      <span className="font-semibold text-slate-900">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Canais de publicação</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Onde cada experiência Toc Toc está presente.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {publishingChannels.map(channel => (
              <div key={channel.label} className="space-y-1">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{channel.label}</span>
                  <span>{channel.value} ativos</span>
                </div>
                <Progress value={Math.min(channel.value * 6, 100)} className="h-2" />
              </div>
            ))}
            <Separator className="bg-slate-200" />
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <Rocket className="h-4 w-4 text-[#FF5605]" />
              Conteúdo distribuído automaticamente para blog, redes sociais e CRM.
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Hierarquia da equipe</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Perfis conectados ao Supabase e responsáveis pelas experiências.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingTeam ? (
              <p className="text-sm text-slate-500">Carregando hierarquia da equipe...</p>
            ) : (
              teamHierarchy.map(item => (
                <div key={item.role} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#3332FC]/10 text-[#3332FC]">
                        <Users className="h-4 w-4" />
                      </span>
                      <p className="text-sm font-semibold text-slate-900">{item.role}</p>
                    </div>
                    <Badge className="bg-[#FF5605]/10 text-xs text-[#FF5605]">
                      {item.members.length} membros
                    </Badge>
                  </div>
                  <ul className="mt-3 space-y-1 text-xs text-slate-500">
                    {item.members.map(member => (
                      <li key={member}>{member}</li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Rotinas inteligentes</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Automatizações que mantêm o ecossistema vivo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {automationStatus.map(item => (
              <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FF5605]/10 text-[#FF5605]">
                      <Target className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                      <p className="text-xs text-slate-500">{item.description}</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-600">{item.status}</Badge>
                </div>
              </div>
            ))}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
              <p>
                Dica: utilize o <strong>Setup do Admin</strong> para habilitar novos acessos e manter a governança de segurança sempre atualizada.
              </p>
              <Button variant="link" className="px-0 text-[#3332FC] hover:text-[#FF5605]" asChild>
                <Link to="/admin/setup">Abrir Setup agora</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </AdminLayout>
  );
}
